/**
 * 1.5.0 shot detection: the workflow editor's part of the Detect shots step (node detect_shots): its icon, the line on
 * the canvas, and its form (the optional overrides; empty: the workspace's setting). Step 0 wrote it whole; S2 owns
 * it now (Builder.tsx, GraphCanvas.tsx and NodeInspector.tsx only call in).
 */
import { ScanLine } from 'lucide-react';
import { SHOT_LIMITS, type AutomationNode } from '@prism/contracts';

type DetectShotsNode = Extract<AutomationNode, { type: 'detect_shots' }>;

export const detectShotsIcon = <ScanLine size={14} />;

export function describeDetectShotsStep(n: DetectShotsNode): string {
  const parts = [
    n.sensitivity != null ? `sensitivity ${n.sensitivity}` : null,
    n.minShotSeconds != null ? `shots ≥ ${n.minShotSeconds} s` : null,
    n.keyframeIntervalSeconds != null ? `a scrub image every ${n.keyframeIntervalSeconds} s in long shots` : null,
  ].filter(Boolean);
  return parts.length ? `Proxy · ${parts.join(' · ')}` : 'Proxy · the workspace’s settings';
}

const FIELDS = [
  { key: 'sensitivity', label: 'Sensitivity', step: 1, help: 'Higher finds more cuts' },
  { key: 'minShotSeconds', label: 'Minimum shot length (s)', step: 0.1, help: 'Cuts closer than this to the previous one are ignored' },
  { key: 'keyframeIntervalSeconds', label: 'Scrub image interval in long shots (s)', step: 1, help: 'Adds scrub images only, never shots' },
] as const;

export function DetectShotsEditor({ node, set }: { node: DetectShotsNode; set: (patch: Partial<AutomationNode>) => void }) {
  return (
    <>
      {FIELDS.map((f) => {
        const lim = SHOT_LIMITS[f.key];
        const v = node[f.key];
        return (
          <div className="field" key={f.key}>
            <label htmlFor={`n-${f.key}`}>{f.label}</label>
            <input id={`n-${f.key}`} className="input" type="number" min={lim.min} max={lim.max} step={f.step} placeholder="The workspace’s"
              value={v ?? ''} data-testid={`detect-shots-${f.key}`}
              onChange={(e) => {
                const raw = e.target.value.trim();
                const num = raw === '' ? undefined : Math.min(lim.max, Math.max(lim.min, Number(raw)));
                set({ [f.key]: num === undefined || Number.isNaN(num) ? undefined : f.key === 'sensitivity' ? Math.round(num) : num } as Partial<AutomationNode>);
              }} />
            <span className="help">{f.help} ({lim.min}–{lim.max}). Empty: the workspace’s setting.</span>
          </div>
        );
      })}
      <span className="help">Remakes the asset’s scrub images and its Shots layer from its proxy. Log layers are left as they are.</span>
    </>
  );
}
